/**
 * The practice deep-link contract (pedagogy spec §4.3.9), shared by Progress,
 * the exam wrapper and Today: one builder for the links (`@shared/practiceLink`,
 * re-exported here, because the server builds the same links for Today's extras),
 * one reader for the setup page, so the two cannot drift apart.
 *
 * | Param               | Meaning on `/practice`                                                       |
 * | ------------------- | ---------------------------------------------------------------------------- |
 * | `chapters`, `topics`| preselect; topic mode unless `mode=mixed` comes with chapters                |
 * | `types=numeric,open`| preselects the question types                                                |
 * | `count=4`           | preselects the count (clamped to 1–30)                                       |
 * | `primary=1`         | topic mode, and the batch holds to the chosen topics' own questions          |
 * | `mode=mixed`        | keeps mixed mode for a link that also carries `chapters`                     |
 * | `set=…`             | an exam-wrapper set (step 2.8c); the page starts it at once (`sets/`)        |
 * | `perPoint=13.33`    | `set=timed-pair`: minutes per exam point; absent, the pace is derived        |
 * | `from=<id>`         | `set=…`: the exam session the suggestion came from (`origin.examSessionId`)  |
 *
 * A plain link only fills the form in; the student presses "Starta". A `set`
 * link starts itself, because the result page's "Starta" was the press.
 */
import { PRACTICE_SETS, clampCount, type PracticeSet } from "@shared/practiceLink";
import type { QuestionType } from "@shared/types";
import type { PracticeMode } from "./types";

export {
  DEFAULT_COUNT,
  FOCUSED_COUNT,
  MAX_COUNT,
  MIN_COUNT,
  PRACTICE_SETS,
  clampCount,
  practiceDeepLink,
  type PracticeLink,
  type PracticeSet,
} from "@shared/practiceLink";

const TYPES: readonly QuestionType[] = ["mc", "numeric", "open"];

/** What a link asks the setup form to start with; `null`/empty means "leave the default". */
export interface PracticePreset {
  mode: PracticeMode | null;
  chapters: number[];
  topics: string[];
  types: QuestionType[];
  count: number | null;
  primaryOnly: boolean;
  set: PracticeSet | null;
  /** `set=timed-pair`: minutes per exam point, when the link carries one (> 0). */
  perPoint?: number;
  /** `set=…`: the exam session the suggestion came from, as sent (a short plain id). */
  from?: string;
}

function list(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** A session id as `origin.examSessionId` takes it: short and made of plain characters only. */
const SESSION_ID = /^[A-Za-z0-9_-]{1,80}$/;

/** A pace in minutes per exam point: a positive decimal number, no more than a day per point. */
const PER_POINT = /^\d{1,4}(\.\d{1,3})?$/;

/**
 * Reads the contract from a query string. Unknown types and sets, and a count
 * that is not a number, are ignored rather than rejected: a hand-edited link
 * opens the form with what is valid in it.
 */
export function readPracticeLink(params: URLSearchParams): PracticePreset {
  const chapters = list(params.get("chapters"))
    .map(Number)
    .filter((n) => Number.isInteger(n));
  const topics = list(params.get("topics"));
  const types = list(params.get("types")).filter((value): value is QuestionType =>
    (TYPES as readonly string[]).includes(value),
  );
  const rawCount = params.get("count")?.trim() ?? "";
  const count = /^-?\d+$/.test(rawCount) ? clampCount(rawCount) : null;
  const set = (PRACTICE_SETS as readonly string[]).includes(params.get("set") ?? "")
    ? (params.get("set") as PracticeSet)
    : null;
  const primary = params.get("primary") === "1";
  const rawPerPoint = params.get("perPoint")?.trim() ?? "";
  const perPoint = PER_POINT.test(rawPerPoint) ? Number(rawPerPoint) : 0;
  const from = params.get("from")?.trim() ?? "";

  let mode: PracticeMode | null = null;
  if ((params.get("mode") === "mixed" || set !== null) && chapters.length > 0) {
    // A set is a mixed session (§4.3.9).
    mode = "mixed";
  } else if (primary || chapters.length > 0 || topics.length > 0) {
    mode = "topic";
  }
  return {
    mode,
    chapters,
    topics: mode === "mixed" ? [] : topics,
    types,
    count,
    primaryOnly: primary && mode === "topic",
    set,
    ...(set === "timed-pair" && perPoint > 0 ? { perPoint } : {}),
    ...(set !== null && SESSION_ID.test(from) ? { from } : {}),
  };
}
